import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ContentLoaderPlaceholder from '@welldev-group/rcl-content-loader-placeholder';
import { I18nText, EmphasisTag } from '@welldev-group/react-comp-lib';
import Button from '@welldev-group/rcl-button';

import ChannelCreateModal from './ChannelCreateModal';
import ChannelEditModal from './ChannelEditModal';
import ChannelDeleteModal from './ChannelDeleteModal';
import EmptyTableContent from '../../../shared/components/EmptyTableContent';

const ChannelTable = ({
  workspaceCredentials,
  workspaceOptions,
  supplierOptions,
  affiliateId,
  fetchCredentials,
  isLoading,
  actionDisabled,
}) => {
  const [isSupplierSelected, setIsSupplierSelected] = useState(false);
  const [supplierSpecific, setSupplierSpecific] = useState(null);
  const [supplierParams, setSupplierParams] = useState([]);
  const [identifier, setIdentifier] = useState(null);
  const [forFlight, setForFlight] = useState(null);
  const [forHotel, setForHotel] = useState(null);
  const [forCar, setForCar] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [canEnableOrDisable, setCanEnableOrDisable] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const setSupplierSpecificAttributesOnFetch = data => {
    setIsSupplierSelected(data.isSupplierSelected);
    setSupplierSpecific(data.supplierSpecific);
    setSupplierParams(data.allowedFields);
    setIdentifier(data.supplierSpecific.accountIdentifier);
    setForFlight(data.supplierSpecific.forFlight);
    setForHotel(data.supplierSpecific.forHotel);
    setForCar(data.supplierSpecific.forCar);
    setEnabled(data.enabled);
    setCanEnableOrDisable(data.canEnableOrDisable);
    setIsFetching(false);
  };

  const setIdentifierOnChange = value => {
    setIdentifier(value);
  };

  const setForFlightOnChange = value => {
    setForFlight(value);
  };

  const setForHotelOnChange = value => {
    setForHotel(value);
  };

  const setForCarOnChange = value => {
    setForCar(value);
  };

  const setSupplierParamsOnChange = value => {
    setSupplierParams(value);
  };

  const setIsFetchingOnChange = value => {
    setIsFetching(value);
  };

  const clearStateOnModalClose = () => {
    setIsSupplierSelected(false);
    setSupplierSpecific(null);
    setSupplierParams([]);
    setIdentifier(null);
    setForFlight(null);
    setForHotel(null);
    setForCar(null);
    setIsFetching(false);
    setEnabled(false);
    setCanEnableOrDisable(false);
  };

  return (
    <>
      <>
        {workspaceCredentials?.length > 0 && (
          <div className="col-12 channel-table__header-container">
            <div className="channel-table__title">
              <I18nText id="channel.table.header.channels" />
            </div>
            <div className="channel-table__add-button">
              <Button
                className="button button--v2 button--small button--primary modal-toggle"
                version="v2"
                size="small"
                fullWidth={true}
                type="primary"
                label={<I18nText id="channel.create_modal.link_text" />}
                onClick={() => {
                  setModalOpen(true);
                }}
                disabled={actionDisabled}
              />
            </div>
          </div>
        )}
        <div className="col-12 channel-table__container">
          {workspaceCredentials?.map(workspaceCredential => (
            <div key={workspaceCredential.workspaceIdentifier}>
              <div className="channel-table__workspace-identifier">
                {workspaceCredential.workspaceIdentifier}
              </div>
              <div className="col-12 channel-table__wrapper">
                <div className="channel-table__row">
                  <div className="channel-table__row--header">
                    <div className="grid">
                      <div className="col-lg-3 col-3 order-1 channel-table__row-item channel-table__supplier-field">
                        <I18nText id="channel.table.header.supplier" />
                      </div>
                      <div className="col-lg-3 col-3 order-3 channel-table__row-item channel-table__center-field">
                        <I18nText id="channel.table.header.account_identifier" />
                      </div>
                      <div className="col-lg-3 col-3 order-3 channel-table__row-item channel-table__center-field" />
                      <div className="col-lg-3 col-3 order-3 channel-table__row-item">
                        <I18nText id="channel.table.header.actions" />
                      </div>
                    </div>
                  </div>
                </div>
                {isLoading ? (
                  <ContentLoaderPlaceholder
                    numberOfLines={2}
                    showBackground={false}
                  />
                ) : (
                  <>
                    {workspaceCredential.credentials.length > 0 ? (
                      workspaceCredential.credentials.map(credential => (
                        <div
                          className="channel-table__row"
                          key={credential.accountIdentifier}
                        >
                          <div className="grid">
                            <div className="col-lg-3 col-md-3 col-6 order-1 channel-table__row-item channel-table__supplier-field">
                              {I18n.t(credential.supplier, {
                                scope: 'components.channel.names',
                              })}
                              {credential.ndc && (
                                <span className="channel-table__row-item-emphasis-tag">
                                  <EmphasisTag
                                    text={<I18nText id="channel.label.ndc" />}
                                    type="accent"
                                    size="tiny"
                                  />
                                </span>
                              )}
                              <div className="channel-table__row-item-header">
                                <I18nText id="channel.table.header.supplier" />
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-3 col-12 order-md-2 order-2 channel-table__row-item channel-table__center-field">
                              {credential.accountIdentifier}
                              <div className="channel-table__row-item-header">
                                <I18nText id="channel.table.header.account_identifier" />
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-3 col-12 order-md-2 order-2 channel-table__row-item channel-table__center-field">
                              {credential.enabled ? (
                                <EmphasisTag
                                  type="success"
                                  size="small"
                                  text={<I18nText id="channel.label.enabled" />}
                                />
                              ) : (
                                <EmphasisTag
                                  type="disabled"
                                  size="small"
                                  text={
                                    <I18nText id="channel.label.disabled" />
                                  }
                                />
                              )}
                            </div>
                            <div className="col-lg-3 col-md-3 col-6 order-md-3 order-1 channel-table__row-item">
                              <div className="channel-table__action-buttons">
                                <ChannelEditModal
                                  fetchCredentials={fetchCredentials}
                                  affiliateId={affiliateId}
                                  supplier={credential.supplier}
                                  id={credential.id}
                                  humanizedSupplier={I18n.t(
                                    credential.supplier,
                                    {
                                      scope: 'components.channel.names',
                                    },
                                  )}
                                  supplierSpecific={supplierSpecific}
                                  setIdentifierOnChange={setIdentifierOnChange}
                                  identifier={identifier}
                                  setForFlightOnChange={setForFlightOnChange}
                                  forFlight={forFlight}
                                  setForHotelOnChange={setForHotelOnChange}
                                  forHotel={forHotel}
                                  setForCarOnChange={setForCarOnChange}
                                  forCar={forCar}
                                  setSupplierParamsOnChange={
                                    setSupplierParamsOnChange
                                  }
                                  supplierParams={supplierParams}
                                  clearStateOnModalClose={
                                    clearStateOnModalClose
                                  }
                                  isSupplierSelected={isSupplierSelected}
                                  setSupplierSpecificAttributesOnFetch={
                                    setSupplierSpecificAttributesOnFetch
                                  }
                                  isFetching={isFetching}
                                  setIsFetchingOnChange={setIsFetchingOnChange}
                                  enabled={enabled}
                                  setEnabled={setEnabled}
                                  canEnableOrDisable={canEnableOrDisable}
                                  disabled={actionDisabled}
                                />
                                <ChannelDeleteModal
                                  fetchCredentials={fetchCredentials}
                                  affiliateId={affiliateId}
                                  supplier={credential.supplier}
                                  id={credential.id}
                                  humanizedSupplier={I18n.t(
                                    credential.supplier,
                                    {
                                      scope: 'components.channel.names',
                                    },
                                  )}
                                  disabled={actionDisabled}
                                />
                              </div>
                              <div className="channel-table__row-item-header">
                                <I18nText id="channel.table.header.actions" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <EmptyTableContent />
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
      <ChannelCreateModal
        workspaceOptions={workspaceOptions}
        supplierOptions={supplierOptions}
        fetchCredentials={fetchCredentials}
        affiliateId={affiliateId}
        supplierSpecific={supplierSpecific}
        setIdentifierOnChange={setIdentifierOnChange}
        identifier={identifier}
        setForFlightOnChange={setForFlightOnChange}
        forFlight={forFlight}
        setForHotelOnChange={setForHotelOnChange}
        forHotel={forHotel}
        setForCarOnChange={setForCarOnChange}
        forCar={forCar}
        setSupplierParamsOnChange={setSupplierParamsOnChange}
        supplierParams={supplierParams}
        clearStateOnModalClose={clearStateOnModalClose}
        isSupplierSelected={isSupplierSelected}
        setSupplierSpecificAttributesOnFetch={
          setSupplierSpecificAttributesOnFetch
        }
        isFetching={isFetching}
        setIsFetchingOnChange={setIsFetchingOnChange}
        enabled={enabled}
        setEnabled={setEnabled}
        modalOpen={modalOpen}
        modalClose={() => {
          setModalOpen(false);
        }}
      />
    </>
  );
};

ChannelTable.defaultProps = {
  actionDisabled: false,
};

ChannelTable.propTypes = {
  fetchCredentials: PropTypes.func.isRequired,
  affiliateId: PropTypes.number.isRequired,
  workspaceCredentials: PropTypes.arrayOf(
    PropTypes.shape({
      workspaceId: PropTypes.number.isRequired,
      workspaceIdentifier: PropTypes.string.isRequired,
      credentials: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          accountIdentifier: PropTypes.string.isRequired,
          supplier: PropTypes.string.isRequired,
          ndc: PropTypes.bool.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  supplierOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  workspaceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  actionDisabled: PropTypes.bool,
};

export default ChannelTable;
